import React, { useMemo, useState } from 'react';
import DataTable from 'components/DataTable';
import { showError, showSuccess } from 'utils';
import { Loading } from '../../../components/loading/Loading';
import { tableStyles } from '../../../css/tableStyles';
import styles from './index.module.scss';
import ActionButton from 'components/ActionButton';
import { addInventoryItemsToSalesChannel } from 'services/api/salesChannel';
import SearchBar from 'components/Autosuggest/Autosuggest';
import { searchItems } from 'services';
import DeleteActionForTable from 'components/DeleteActionForTableData';

const AddInventoryItemsToSalesChannel = (props) => {
  const { salesChannelId, fetchSalesChannelInventoryItemsFn } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedInventoryItems, setSelectedInventoryItems] = useState(
    [],
  ) as any;
  const [searchTerm, setSearchTerm] = useState('');

  const handleSuggestionClick = (e) => {
    const preSelectedItem = selectedInventoryItems?.filter((item) => {
      return e?.id === item?.id;
    });

    if (preSelectedItem?.length > 0) {
      showError(`${e?.displayName} is already selected`);
      return;
    }
    setSelectedInventoryItems([...selectedInventoryItems, e]);
    setSearchTerm('');
  };

  const onClickSave = async () => {
    try {
      setLoading(true);
      const inventoryItemIds = selectedInventoryItems?.map((item) => {
        return item?.id;
      });
      await addInventoryItemsToSalesChannel({
        salesChannelId,
        inventoryItemIds,
      });
      await fetchSalesChannelInventoryItemsFn();
      setSelectedInventoryItems([]);
      showSuccess('Item(s) added successfully');
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const removeInventoryItem = (itemId) => {
    const newSelectedInventoryItems = selectedInventoryItems?.filter((item)=>item?.id !== itemId);
    setSelectedInventoryItems(newSelectedInventoryItems);
  };
  
  const columns = useMemo(
    () => [
      {
        name: 'Id',
        selector: 'id',
        sortable: false,
        // eslint-disable-next-line react/display-name
        format: (row) => <div className={styles['name']}>{row.id}</div>,
      },
      {
        name: 'Image',
        selector: 'image',
        sortable: true,
        // eslint-disable-next-line react/display-name
        format: (row) => (
          <img
            className={styles['item-image']}
            src={row.image}
            alt={'item-image'}
          />
        ),
      },
      {
        name: 'Name',
        selector: 'displayName',
        sortable: true,
        // eslint-disable-next-line react/display-name
        format: (row) => (
          <div className={styles['name']}>{row.displayName}</div>
        ),
      },
      {
        name: 'Category',
        selector: 'category.displayName',
        sortable: true,
        // eslint-disable-next-line react/display-name
        format: (row) => (
          <div className={styles['name']}>{row.category?.displayName}</div>
        ),
      },
      {
        name: 'Price',
        selector: 'price',
        sortable: true,
        // eslint-disable-next-line react/display-name
        format: (row) => <div className={styles['name']}>{row.price}</div>,
      },
      {
        name: 'Action',
        selector: '',
        sortable: true,
        width: '100px',
        // eslint-disable-next-line react/display-name
        cell: (row: Record<string, any>) => (
          <div className={'ml-3'}>
            <DeleteActionForTable
              onClick={() => removeInventoryItem(row?.id)}
            />
          </div>
        ),
      },
    ],
    [selectedInventoryItems],
  );

  const getSaveButton = () => {
    if(selectedInventoryItems?.length === 0) return null;
    return (
      <div className={styles['save-btn-container']}>
        <ActionButton
          text={'Add to channel'}
          active={!loading}
          isLoading={loading}
          onClick={onClickSave}
        />
      </div>
    );
  };

  const getSelectedInventoryItems = () => {
    if(selectedInventoryItems?.length === 0) return null;
    return(
      <div className={styles['inventory-items-container']}>
        <div className={styles['section-header']}>
          <div className={styles['section-heading']}>
            {' '}
              Selected Inventory Items
          </div>
        </div>
        <div style={{ padding: '20px 15px' }}>
          <DataTable
            keyField="objectId"
            columns={columns}
            noHeader={true}
            data={selectedInventoryItems}
            highlightOnHover={true}
            customStyles={tableStyles}
          />
        </div>
      </div>
    );
  };

  return (
    <div>
      {loading && <Loading />}
      <div className={styles['add-inventory-items-container']}>
        <div className={styles['autosuggest-group']}>
          <div className={styles['label']}>Search Inventory Item to add</div>
          <div className={styles['autosuggest-input']}>
            <SearchBar
              value={searchTerm}
              setValue={(value) => {
                setSearchTerm(value);
              }}
              searchAPI={searchItems}
              searchAPIParamKey={'term'}
              onSuggestionClicked={handleSuggestionClick}
            />
          </div>
        </div>
        {getSelectedInventoryItems()}
        {getSaveButton()}
      </div>
    </div>
  );
};

export { AddInventoryItemsToSalesChannel };
